import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { FiEdit2, FiPlus, FiSearch, FiTrash2 } from "react-icons/fi";
import { LuLandmark } from "react-icons/lu";

import ListPageShell from "../list-page/ListPageShell";
import ListPageHeader from "../list-page/ListPageHeader";
import ListPageSearch from "../list-page/ListPageSearch";
import ListPageEmpty from "../list-page/ListPageEmpty";
import DeleteConfirm from "../DeleteConfirm";
import BankFormModal from "./BankFormModal";
import { openModalWithTimeout } from "../../store/alertSlice";
import {
  bankMutationFailed,
  useCreateBankMutation,
  useDeleteBankMutation,
  useGetBanksQuery,
  useUpdateBankMutation,
} from "../../store/services/bankServices";

const SkeletonRow = () => (
  <tr className="animate-pulse">
    <td className="px-6 py-4"><div className="h-3 w-8 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-48 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-7 w-16 bg-gray-100 rounded ml-auto" /></td>
  </tr>
);

const IconButton = ({ label, onClick, danger, children }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    onClick={onClick}
    className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition ${
      danger ? "text-gray-400 hover:text-red-600 hover:bg-red-50" : "text-gray-400 hover:text-gray-900 hover:bg-gray-100"
    }`}
  >
    {children}
  </button>
);

/* Banks citizens can pick when registering (admin only, like the backend's /admin/banks) */
export default function BanksMain() {
  const dispatch = useDispatch();
  const { data: banks = [], isLoading, isFetching } = useGetBanksQuery();
  const [createBank, { isLoading: isCreating }] = useCreateBankMutation();
  const [updateBank, { isLoading: isUpdating }] = useUpdateBankMutation();
  const [deleteBank] = useDeleteBankMutation();

  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ isOpen: false, bank: null });
  const [toDelete, setToDelete] = useState(null);

  const alert = (text, type) => dispatch(openModalWithTimeout({ text, type }, 3000));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...banks].sort((a, b) => a.name.localeCompare(b.name));
    return q ? sorted.filter((b) => b.name.toLowerCase().includes(q) || String(b.id) === q) : sorted;
  }, [banks, search]);

  const handleSubmit = async (name) => {
    const result = form.bank
      ? await updateBank({ id: form.bank.id, name })
      : await createBank({ name });
    if (bankMutationFailed(result)) {
      alert(form.bank ? "Ndryshimi i bankës dështoi!" : "Shtimi i bankës dështoi!", "error");
      return;
    }
    alert(form.bank ? "Banka u ndryshua me sukses!" : "Banka u shtua me sukses!", "success");
    setForm({ isOpen: false, bank: null });
  };

  const handleDelete = async () => {
    const bank = toDelete;
    setToDelete(null);
    const result = await deleteBank({ id: bank.id });
    if (bankMutationFailed(result)) {
      alert("Fshirja e bankës dështoi!", "error");
      return;
    }
    alert("Banka u fshi me sukses!", "success");
  };

  const isEmpty = !isLoading && filtered.length === 0;

  return (
    <>
      <BankFormModal
        isOpen={form.isOpen}
        bank={form.bank}
        existingNames={banks.map((b) => b.name)}
        isSaving={isCreating || isUpdating}
        onClose={() => setForm({ isOpen: false, bank: null })}
        onSubmit={handleSubmit}
      />
      <DeleteConfirm
        title={`Fshi bankën "${toDelete?.name || ""}"?`}
        description="Banka nuk do të shfaqet më gjatë regjistrimit. Qytetarët që e kanë zgjedhur e ruajnë llogarinë e tyre."
        isOpen={!!toDelete}
        closeModal={() => setToDelete(null)}
        handleConfirm={handleDelete}
      />

      <ListPageShell>
        <ListPageHeader
          eyebrow={{ icon: <LuLandmark size={18} />, label: "Bankat" }}
          title="Bankat"
          subtitle={banks.length ? `${banks.length} banka të disponueshme për qytetarët` : "Bankat që qytetarët mund të zgjedhin"}
          actions={
            <button
              type="button"
              onClick={() => setForm({ isOpen: true, bank: null })}
              className="inline-flex items-center gap-2 rounded-lg bg-[var(--secondary-color)] hover:bg-[var(--secondary-color)]/90 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition"
            >
              <FiPlus size={16} /> Shto bankë
            </button>
          }
        />

        <ListPageSearch
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Kërko sipas emrit ose ID-së..."
          hasFilters={!!search}
          onClearFilters={() => setSearch("")}
        />

        {isEmpty ? (
          <ListPageEmpty
            icon={search ? FiSearch : LuLandmark}
            title={search ? "Asnjë bankë nuk u gjet" : "Ende asnjë bankë"}
            subtitle={search ? "Provoni një emër tjetër." : "Shtoni bankat që qytetarët do t'i zgjedhin gjatë regjistrimit."}
            action={
              !search && (
                <button
                  type="button"
                  onClick={() => setForm({ isOpen: true, bank: null })}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition"
                >
                  <FiPlus size={14} /> Shto bankë
                </button>
              )
            }
          />
        ) : (
          <div className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.03)] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="w-[12%] text-left px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">ID</th>
                  <th className="text-left px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Emri</th>
                  <th className="w-[120px] px-6 py-3.5" />
                </tr>
              </thead>
              <tbody className={`divide-y divide-gray-100 ${isFetching && !isLoading ? "opacity-60" : ""} transition-opacity`}>
                {isLoading
                  ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
                  : filtered.map((bank) => (
                      <tr key={bank.id} className="group hover:bg-gray-50/70 transition-colors">
                        <td className="px-6 py-3 text-gray-500 tabular-nums text-xs">#{bank.id}</td>
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-3">
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 text-gray-500 shrink-0">
                              <LuLandmark size={15} />
                            </span>
                            <span className="font-medium text-gray-900">{bank.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <IconButton label="Ndrysho" onClick={() => setForm({ isOpen: true, bank })}>
                              <FiEdit2 size={15} />
                            </IconButton>
                            <IconButton label="Fshi" danger onClick={() => setToDelete(bank)}>
                              <FiTrash2 size={15} />
                            </IconButton>
                          </div>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        )}
      </ListPageShell>
    </>
  );
}
